import { useEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import QRCode from 'qrcode';
import './login.css';
import {
  loginSchema, isChallengeRequired, isDestinationPickRequired, isMfaEnrollRequired,
  type LoginRequest, type LoginChallengeRequired, type LoginDestinationPickRequired,
  type LoginMfaEnrollRequired, type LoginMode,
} from './types';
import {
  useLogin, useVerifyChallenge, useSelectDestination,
  useStartMfaEnrollAtLogin, useVerifyMfaEnrollAtLogin,
} from './mutations';
import { resendTwoFactor, getLoginConfig } from './api';
import { THEMES, type LoginTheme } from './themes';
import { applyAppTheme, PENDING_THEME_SAVE_KEY } from '../../theme/useAppTheme';
import { AppDialog } from '../../components/AppDialog';
import { useHotkeys } from '../../components/hotkeys';
import { warn } from '../../components/confirm';
import { ComboSelect } from '../../components/ComboSelect';

const THEME_KEY      = 'alpaca.login.theme';
const REMEMBERED_KEY = 'alpaca.login.remembered';

/**
 * Translate an axios / auth error into a user-friendly message.
 *
 * Default axios errors come through as "Request failed with status code 401",
 * which is meaningless to a lab analyst.  This helper inspects the response
 * body (if any) for a server-provided message, then falls back to a
 * status-code-keyed dictionary.  User feedback 2026-05-31: "password is wrong,
 * need to show the correct message not the error code".
 */
function friendlyAuthError(err: unknown): string {
  // Server-provided error message wins.
  const e = err as {
    response?: {
      status?: number;
      data?:
        | { error?: { message?: string; code?: string } }
        | { message?: string }
        | string;
    };
    message?: string;
  };
  const body = e?.response?.data;
  if (body && typeof body === 'object') {
    const nested = (body as { error?: { message?: string } }).error?.message;
    if (nested && typeof nested === 'string' && nested.trim().length > 0) return nested;
    const flat = (body as { message?: string }).message;
    if (flat && typeof flat === 'string' && flat.trim().length > 0) return flat;
  }
  const status = e?.response?.status;
  switch (status) {
    case 400: return 'Invalid request — please check the username and password.';
    case 401: return 'Wrong username or password.';
    case 403: return 'Your account does not have permission to sign in.';
    case 423:
    case 429: return 'Too many failed attempts. Wait a minute, then try again.';
    case 500: return 'Something went wrong on the server. Please try again.';
    case 502:
    case 503:
    case 504: return 'The server is unreachable right now. Please try again in a moment.';
  }
  // No HTTP response (network down / CORS / DNS) — common message starts
  // with "Network Error" from axios.
  if (e?.message && /network/i.test(e.message)) {
    return 'Network error — check your connection and try again.';
  }
  // Last resort — show the raw message if it's not the unhelpful axios default.
  if (e?.message && !/^request failed with status code/i.test(e.message)) {
    return e.message;
  }
  return 'Sign-in failed. Please try again.';
}

/** The server's machine-readable `error.code`, when it sent one. */
function authErrorCode(err: unknown): string | null {
  const body = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data;
  const code = body && typeof body === 'object'
    ? (body as { error?: { code?: string } }).error?.code
    : undefined;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/** "Keep me signed in" credentials we hydrate the form with on next visit.
 *  Stored in localStorage when the user checks the box during a successful
 *  login.  Password is included per product requirement — note this is a
 *  security trade-off (any browser-extension or XSS could read it). */
interface RememberedCreds {
  username: string;
  password: string;
}

function readRemembered(): RememberedCreds | null {
  try {
    const raw = localStorage.getItem(REMEMBERED_KEY);
    return raw ? JSON.parse(raw) as RememberedCreds : null;
  } catch { return null; }
}

function initialIndex(): number {
  try {
    const id = localStorage.getItem(THEME_KEY);
    const i = THEMES.findIndex((t) => t.id === id);
    return i >= 0 ? i : 0;
  } catch {
    return 0;
  }
}

function themeVars(t: LoginTheme): Record<string, string> {
  return {
    '--accent': t.accent,
    '--accent-hover': t.accentHover,
    '--accent-glow': t.accentGlow,
    '--panel-left': t.panelLeft,
    '--panel-right': t.panelRight,
    '--text-on-left': t.textOnLeft,
    '--text-muted-left': t.textMutedLeft,
    '--text-dark': t.textDark,
    '--text-muted': t.textMuted,
    '--text-faint': t.textFaint,
    '--input-border': t.inputBorder,
    '--border-strong': t.borderStrong,
    '--input-bg': t.inputBg,
    '--divider': t.divider,
    '--feature-bg': t.featureBg,
    '--feature-border': t.featureBorder,
    '--status-bg': t.statusBg,
    '--status-border': t.statusBorder,
    '--status-text': t.statusText,
  };
}

/**
 * ALPACA Intelligence login — ported from
 * Alpaca Login Page/alpaca_lims_all_themes (7).html. 15 themes via a
 * dropdown switcher; per-theme ALPACA-logo chip; no page scrollbar;
 * responsive. Wired to the legacy-DB auth endpoint.
 */
export function LoginPage() {
  const loginMutation = useLogin();
  const verifyMutation = useVerifyChallenge();
  const selectDestMutation = useSelectDestination();
  const startMfaEnrollMutation = useStartMfaEnrollAtLogin();
  const verifyMfaEnrollMutation = useVerifyMfaEnrollAtLogin();
  const [idx, setIdx] = useState<number>(initialIndex);
  const [open, setOpen] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  // ALPACA parity: admin/service users under a FORCED 'twofactor' mode with
  // more than one registered address must pick which one gets the code
  // (TwoFactorAuthNewViewController's destination dropdown) BEFORE a
  // challenge exists at all.
  const destinationPick = loginMutation.data && isDestinationPickRequired(loginMutation.data)
    ? (loginMutation.data as LoginDestinationPickRequired)
    : null;

  // Mode 'mfa' + no authenticator enrolled on this account yet — /login
  // returns a fresh QR/secret directly instead of a normal challenge.
  const mfaEnrollFromLogin = loginMutation.data && isMfaEnrollRequired(loginMutation.data)
    ? (loginMutation.data as LoginMfaEnrollRequired)
    : null;
  // "Add another authenticator" — triggered FROM an already-active MFA
  // challenge (shared account, e.g. "service"); replaces the code-entry view
  // with a fresh QR of its own until submitted.
  const mfaEnrollActive = mfaEnrollFromLogin ?? startMfaEnrollMutation.data ?? null;
  const [mfaQr, setMfaQr] = useState<string | null>(null);
  useEffect(() => {
    if (!mfaEnrollActive) { setMfaQr(null); return; }
    let alive = true;
    QRCode.toDataURL(mfaEnrollActive.otpAuthUri, { errorCorrectionLevel: 'M', margin: 1, width: 220 })
      .then((url) => { if (alive) setMfaQr(url); })
      .catch(() => { if (alive) setMfaQr(null); });
    return () => { alive = false; };
  }, [mfaEnrollActive?.otpAuthUri]);

  // Challenge state.  When the server tells us 2FA / MFA is required, we
  // park the descriptor here and the form swaps into the OTP-entry step
  // without ever leaving /login (so the theme + brand panel persist).
  // A challenge can arrive directly from /login OR — one step later — from
  // /2fa/select-destination once the user picked an address.
  const challenge = selectDestMutation.data
    ?? (loginMutation.data && isChallengeRequired(loginMutation.data)
      ? (loginMutation.data as LoginChallengeRequired)
      : null);
  const [activeChallengeId, setActiveChallengeId] = useState<string | null>(null);
  const [activeChannelDesc, setActiveChannelDesc] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  // Member name captured at MFA enrolment — stamped on the secret so a shared
  // account (service) can attribute an e-signature to the real person.
  const [enrollLabel, setEnrollLabel] = useState('');
  const [rememberAtChallenge, setRememberAtChallenge] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Global login mode (Settings → Default Settings → Login Options) — fetched
  // anonymously so the form can hint that a second step will follow.  The
  // server FORCES the challenge regardless; this is display-only.
  const [loginMode, setLoginMode] = useState<LoginMode>('standard');
  useEffect(() => {
    let alive = true;
    getLoginConfig()
      .then((cfg) => { if (alive) setLoginMode(cfg.loginMode); })
      .catch(() => { /* unreachable API — the hint just stays hidden */ });
    return () => { alive = false; };
  }, []);
  const modeHint =
    loginMode === 'twofactor' ? 'Two-factor code will be required (sent to your email).'
    : loginMode === 'mfa' ? 'Authenticator code will be required.'
    : null;

  useEffect(() => {
    if (!challenge) return;
    setActiveChallengeId(challenge.challengeId);
    setActiveChannelDesc(
      challenge.requires === 'mfa'
        ? 'authenticator app'
        : `${challenge.channel === 'Email' ? 'email' : 'phone'} ${challenge.maskedDestination ?? ''}`.trim(),
    );
    setRememberAtChallenge(!!challenge.rememberMe);
    setSecondsLeft(challenge.expiresInSeconds ?? 300);
    setOtpCode('');
  }, [challenge]);

  // Live countdown timer for the OTP expiry.
  useEffect(() => {
    if (!activeChallengeId || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [activeChallengeId, secondsLeft]);

  // Clear the code field whenever a NEW MFA-enroll challenge appears (either
  // the first-time one from /login, or a fresh "add another device" one).
  useEffect(() => { if (mfaEnrollActive) setOtpCode(''); }, [mfaEnrollActive?.challengeId]);

  const resetToCredentials = () => {
    loginMutation.reset();
    verifyMutation.reset();
    selectDestMutation.reset();
    startMfaEnrollMutation.reset();
    verifyMfaEnrollMutation.reset();
    setActiveChallengeId(null);
    setActiveChannelDesc(null);
    setOtpCode('');
  };

  const theme = THEMES[idx]!;
  const onDarkLeft = theme.alpacaWrap.includes('background:#fff');

  // Pre-fill from localStorage when the user previously checked "Keep me signed in".
  const remembered = useMemo(() => readRemembered(), []);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username:   remembered?.username ?? '',
      password:   remembered?.password ?? '',
      rememberMe: remembered != null,   // box stays checked when creds remembered
    },
  });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  const pick = (i: number) => {
    setIdx(i);
    setOpen(false);
    // Push CSS vars to :root + persist + fire the global theme-changed
    // event so every form's chrome (popup title bars, table headers,
    // accent buttons) updates immediately — even before the user logs in.
    applyAppTheme(THEMES[i]!);
    // Remember this pick so AppShell saves it to the per-user store right after
    // login (we have no JWT here to save it now).
    try { localStorage.setItem(PENDING_THEME_SAVE_KEY, THEMES[i]!.id); } catch { /* ignore */ }
  };

  const sceneStyle = useMemo(
    () => themeVars(theme) as unknown as CSSProperties,
    [theme],
  );
  const glow = useMemo(
    () => `radial-gradient(ellipse at 80% 20%, ${theme.accent}22 0%, transparent 60%),`
        + ` radial-gradient(ellipse at 10% 90%, ${theme.accent}11 0%, transparent 50%)`,
    [theme],
  );
  const alpacaWrapStyle: CSSProperties = onDarkLeft
    ? { background: '#fff', borderRadius: 12, padding: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }
    : { background: 'transparent' };
  const alpacaBlend = onDarkLeft ? 'normal' : 'multiply';

  // SEAT_LIMIT is a BLOCKED ACTION, not a bad credential — the licence, not the
  // password, refused them.  BO UI Standard §13 tier 1: a blocking message goes
  // in the shared centred MODAL (which must be acknowledged), never the inline
  // banner that sits under the heading and is easy to miss.  Raised for every
  // login leg, since the seat is reserved at each token-issue exit (standard,
  // 2FA/MFA verify, and MFA enrol) — see AuthController.ReserveSeatAsync.
  const seatBlocked = [loginMutation, verifyMutation, verifyMfaEnrollMutation]
    .find((m) => m.isError && authErrorCode(m.error) === 'SEAT_LIMIT');
  useEffect(() => {
    if (!seatBlocked) return;
    const message = friendlyAuthError(seatBlocked.error);
    // Reset FIRST so the inline banner never flashes behind the modal, and so
    // dismissing it leaves a clean form the user can immediately retry from.
    seatBlocked.reset();
    void warn(message, { title: 'Concurrent user limit reached' });
  }, [seatBlocked]);

  // Rule 14 discoverability: Enter signs in. That is the form's NATIVE submit —
  // the two fields sit in a <form> whose only submit button is Sign In (the
  // show/hide-password toggle is type="button" so it cannot steal Enter) — so
  // this entry is DISPLAY-ONLY: the `?` sheet lists "Sign In · Enter" and the
  // hotkey store never handles the key itself (owner question 2026-09-26).
  useHotkeys([{ combo: 'enter', label: 'Sign In', group: 'Sign In', order: 1, displayOnly: true, run: () => {} }]);

  const onSubmit = (v: LoginRequest) => {
    // Persist (or wipe) the remembered credentials BEFORE we kick off the
    // login request — so even if the request fails the user's next attempt
    // still has the fields pre-filled when they checked the box.
    try {
      if (v.rememberMe) {
        localStorage.setItem(REMEMBERED_KEY, JSON.stringify({ username: v.username, password: v.password }));
      } else {
        localStorage.removeItem(REMEMBERED_KEY);
      }
    } catch { /* storage disabled — silently no-op */ }
    loginMutation.mutate(v);
  };
  const busy = isSubmitting || loginMutation.isPending;

  return (
    <div className="login-scene" style={sceneStyle}>
      <div className="scene">
        {/* Brand panel */}
        <div className="brand-panel">
          <div className="brand-glow" style={{ background: glow }} />
          <div className="logo-lockup">
            <div className="logo-mark" style={alpacaWrapStyle}>
              <img
                src={`${import.meta.env.BASE_URL}alpaca-logo.jpg`}
                alt="ALPACA"
                style={{ mixBlendMode: alpacaBlend as CSSProperties['mixBlendMode'] }}
              />
            </div>
            <div className="logo-text">
              <span className="logo-name">ALPACA <span>LIMS G6</span></span>
              <span className="logo-product">Laboratory Information Management System</span>
            </div>
          </div>

          <div className="brand-hero">
            <h1 className="brand-tagline">Built by Chemists,<br /><em>for Chemists.</em></h1>
            <div className="brand-rule" />
            <p className="brand-sub">
              A modern laboratory information management system built for
              analytical chemistry workflows — from sample intake to validated
              results.
            </p>
          </div>

          <ul className="feature-list">
            <li className="feature-item">
              <div className="feature-icon"><svg viewBox="0 0 24 24"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg></div>
              <div className="feature-copy"><strong>Analysts</strong><span>Efficient and accurate result entry with intelligent validation</span></div>
            </li>
            <li className="feature-item">
              <div className="feature-icon"><svg viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg></div>
              <div className="feature-copy"><strong>Managers</strong><span>Real-time visibility, control, and compliance reporting</span></div>
            </li>
            <li className="feature-item">
              <div className="feature-icon"><svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></div>
              <div className="feature-copy"><strong>Clients</strong><span>Secure access to certificates and reports, anytime</span></div>
            </li>
          </ul>
        </div>

        {/* Form panel — the credentials form ALWAYS stays mounted underneath;
            2FA/MFA is a POPUP layered on top (ALPACA parity: legacy shows
            TwoFactorAuthNew as a modal PopupWindowShowAction, not a full-page
            swap — see Module.cs GetStartupActions). */}
        <div className="form-panel">
            <form className="form-container" onSubmit={handleSubmit(onSubmit)} noValidate>
              <p className="form-eyebrow">Welcome back</p>
              <h2 className="form-title">Sign In</h2>
              <p className="form-subtitle">Enter your credentials to access the lab.</p>

              {loginMutation.isError && (
                <div className="err" role="alert">{friendlyAuthError(loginMutation.error)}</div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="username">Username</label>
                <div className="input-wrap">
                  <span className="input-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg></span>
                  <input id="username" className="form-input" type="text" placeholder="your.username" autoComplete="username" {...register('username')} />
                </div>
                {errors.username && <div className="field-err">{errors.username.message}</div>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">Password</label>
                <div className="input-wrap">
                  <span className="input-icon"><svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg></span>
                  <input id="password" className="form-input" type={showPw ? 'text' : 'password'} placeholder="••••••••" autoComplete="current-password" {...register('password')} />
                  <button type="button" className="toggle-pw" aria-label={showPw ? 'Hide password' : 'Show password'} onClick={() => setShowPw((v) => !v)}>
                    {showPw ? (
                      <svg viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                {errors.password && <div className="field-err">{errors.password.message}</div>}
              </div>

              <div className="form-row">
                <label className="checkbox-wrap">
                  <input type="checkbox" {...register('rememberMe')} />
                  <span className="checkbox-label">Keep me signed in</span>
                </label>
                <a href="#" className="forgot-link">Forgot password?</a>
              </div>

              {modeHint && (
                <div className="field-err" style={{ color: 'var(--ai-muted)', marginBottom: 8 }} role="note">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ verticalAlign: 'middle', marginRight: 5 }}>
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  {modeHint}
                </div>
              )}

              <button className="btn-signin" type="submit" disabled={busy}>
                {busy ? (
                  <>
                    <svg className="spin" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
                    <span>Signing in…</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </>
                )}
              </button>

              <div className="security-note">
                <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                <span>Authorized personnel only · All access is monitored and logged</span>
              </div>
            </form>

          <div className="btsoft-footer">
            <p>Powered by</p>
            <div className="btsoft-wrap">
              <img src={`${import.meta.env.BASE_URL}btsoft-logo.jpg`} alt="BTSoft" style={{ mixBlendMode: 'multiply' }} />
            </div>
          </div>
        </div>
      </div>

      {/* 2FA / MFA popup — ALPACA parity (TwoFactorAuthNew shown as a modal
          PopupWindowShowAction, single screen: destination picker + "send
          passcode" + OTP field together — see TwoFactorAuthNewViewController).
          Layers over the credentials form; closing it (✕ / backdrop / Cancel)
          returns here. */}
      <AppDialog
        open={!!destinationPick || !!activeChallengeId || !!mfaEnrollFromLogin}
        title={
          mfaEnrollActive ? 'Set Up Authenticator App'
          : challenge?.requires === 'mfa' ? 'Authenticator Verification'
          : 'Send a text passcode'
        }
        icon="ti-shield-lock"
        onClose={resetToCredentials}
        width={560}
        footer={
          <>
            <button type="button" className="ca-btn" onClick={resetToCredentials}>Cancel</button>
            <button
              type="submit" form="otp-form" className="ca-btn ca-btn-solid"
              disabled={
                (!activeChallengeId && !mfaEnrollActive)
                || verifyMutation.isPending || verifyMfaEnrollMutation.isPending
                || otpCode.length < 6
              }
            >
              {(verifyMutation.isPending || verifyMfaEnrollMutation.isPending) ? 'Verifying…' : 'Submit'}
            </button>
          </>
        }
      >
        {mfaEnrollActive ? (
          <MfaEnrollBody
            qr={mfaQr}
            secretBase32={mfaEnrollActive.secretBase32}
            code={otpCode}
            setCode={setOtpCode}
            label={enrollLabel}
            setLabel={setEnrollLabel}
            account={mfaEnrollActive.account}
            error={verifyMfaEnrollMutation.error ? friendlyAuthError(verifyMfaEnrollMutation.error) : null}
            onSubmit={(code) => verifyMfaEnrollMutation.mutate({
              challengeId: mfaEnrollActive.challengeId,
              code,
              rememberMe: mfaEnrollFromLogin?.rememberMe ?? rememberAtChallenge,
              label: enrollLabel,
            })}
          />
        ) : (
          <TwoFactorBody
            isTotp={challenge?.requires === 'mfa'}
            destinations={destinationPick?.destinations ?? null}
            channel={destinationPick?.channel ?? challenge?.channel ?? 'Email'}
            sendBusy={selectDestMutation.isPending}
            sendError={selectDestMutation.error ? friendlyAuthError(selectDestMutation.error) : null}
            onSendCode={(index) => destinationPick && selectDestMutation.mutate({
              pickId: destinationPick.pickId,
              index,
              rememberMe: !!destinationPick.rememberMe,
            })}
            hasChallenge={!!activeChallengeId}
            channelDesc={activeChannelDesc ?? ''}
            expiresIn={secondsLeft}
            code={otpCode}
            setCode={setOtpCode}
            verifyError={verifyMutation.error ? friendlyAuthError(verifyMutation.error) : null}
            onSubmit={(code) => activeChallengeId && verifyMutation.mutate({
              challengeId: activeChallengeId,
              code,
              rememberMe: rememberAtChallenge,
            })}
            onResend={destinationPick ? undefined : async () => {
              if (!activeChallengeId) return;
              try {
                const fresh = await resendTwoFactor(activeChallengeId);
                setActiveChallengeId(fresh.challengeId);
                setSecondsLeft(fresh.expiresInSeconds ?? 300);
                setOtpCode('');
              } catch { /* surfaced via mutation error if user retries */ }
            }}
            onAddMfaDevice={challenge?.requires === 'mfa' && activeChallengeId ? () => {
              startMfaEnrollMutation.mutate({ challengeId: activeChallengeId });
            } : undefined}
            addMfaDeviceBusy={startMfaEnrollMutation.isPending}
          />
        )}
      </AppDialog>

      {/* Theme switcher */}
      <div className="theme-switcher" ref={switcherRef}>
        <button className="theme-btn" type="button" onClick={() => setOpen((o) => !o)} aria-label="Change theme" title="Change theme">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
          </svg>
          <span className="theme-label">{theme.name}</span>
          <svg className={`chevron${open ? ' open' : ''}`} viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {open && (
          <div className="theme-dropdown">
            {THEMES.map((t, i) => (
              <button
                key={t.id}
                type="button"
                className={`theme-option${i === idx ? ' active' : ''}`}
                onClick={() => pick(i)}
              >
                <span className="swatch" style={{ background: t.accent }} />
                {t.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="status-bar">
        <div className="status-left">
          <span className="status-version">ALPACA v26.10.5</span>
          <div className="status-links">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Use</a>
            <a href="#">© 2026 BTSoft Inc.</a>
          </div>
        </div>
        <div className="status-right">
          <div className="status-dot" />
          <span className="status-text">All systems operational</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Two-factor popup body — ALPACA parity (TwoFactorAuthNewViewController /
// "Send a text passcode" popup): ONE screen combining the destination
// dropdown, an inline "Send passcode" button, and the OTP field — not two
// separate popups.  The destination dropdown only renders for admin/service
// users with more than one registered address (destinations != null); a
// regular single-address user goes straight to the OTP field (the code was
// already auto-sent by /login).
// ─────────────────────────────────────────────────────────────────────────────
interface TwoFactorBodyProps {
  isTotp:       boolean;              // true → MFA (authenticator, no send step)
  destinations: string[] | null;      // masked list, or null when no picker applies
  channel:      'Mobile' | 'Email';
  sendBusy:     boolean;
  sendError:    string | null;
  onSendCode:   (index: number) => void;
  hasChallenge: boolean;              // an OTP has been sent — the code field is live
  channelDesc:  string;               // "email sh***@x.com" — where the code went
  expiresIn:    number;               // live countdown (seconds)
  code:         string;
  setCode:      (c: string) => void;
  verifyError:  string | null;
  onSubmit:     (code: string) => void;
  onResend?:    () => void;           // only for the no-picker (auto-sent) case
  /** Set only for MFA challenges — "Add another authenticator" lets a
   *  DIFFERENT person sharing this account (e.g. "service") enroll their own
   *  device instead of entering an existing code. */
  onAddMfaDevice?:   () => void;
  addMfaDeviceBusy?: boolean;
}

/** Dialog BODY only — the AppDialog popup supplies the title/header/close;
 * the Submit/Cancel buttons live in the popup's footer (form="otp-form"). */
function TwoFactorBody({
  isTotp, destinations, channel, sendBusy, sendError, onSendCode,
  hasChallenge, channelDesc, expiresIn, code, setCode, verifyError, onSubmit, onResend,
  onAddMfaDevice, addMfaDeviceBusy,
}: TwoFactorBodyProps) {
  const options = useMemo(
    () => (destinations ?? []).map((d, i) => ({ value: String(i), label: d })),
    [destinations]);
  const [selected, setSelected] = useState('0');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (hasChallenge) inputRef.current?.focus(); }, [hasChallenge]);
  const mm = String(Math.floor(expiresIn / 60)).padStart(1, '0');
  const ss = String(expiresIn % 60).padStart(2, '0');

  if (isTotp) {
    return (
      <form id="otp-form" onSubmit={(e) => { e.preventDefault(); if (code.length >= 6) onSubmit(code); }} noValidate>
        <p className="form-subtitle" style={{ marginTop: 0 }}>
          Open your authenticator app and enter the 6-digit code shown for ALPACA Intelligence.
        </p>
        {verifyError && <div className="err" role="alert">{verifyError}</div>}
        <OtpField inputRef={inputRef} code={code} setCode={setCode} />
        {onAddMfaDevice && (
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="forgot-link"
              onClick={onAddMfaDevice}
              disabled={addMfaDeviceBusy}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {addMfaDeviceBusy ? 'Generating…' : 'Add another authenticator'}
            </button>
          </div>
        )}
      </form>
    );
  }

  return (
    <>
      <p className="form-subtitle" style={{ marginTop: 0 }}>
        Verify it&rsquo;s you by sending a text passcode
      </p>

      {destinations && (
        <form
          id="dest-pick-form"
          onSubmit={(e) => { e.preventDefault(); onSendCode(Number(selected)); }}
          noValidate
          style={{ marginBottom: 14 }}
        >
          {/* ALPACA parity: the channel toggle from TwoFactorAuthNew — only
              Email is wired for real delivery today (SMS has no gateway), so
              it's shown disabled rather than falsely offered as a choice. */}
          <div className="form-row" style={{ gap: 16, marginBottom: 8 }}>
            <label className="checkbox-wrap">
              <input type="radio" checked={channel === 'Email'} readOnly />
              <span className="checkbox-label">E-Mail</span>
            </label>
            <label className="checkbox-wrap" style={{ opacity: 0.5 }}>
              <input type="radio" checked={channel === 'Mobile'} disabled readOnly />
              <span className="checkbox-label">SMS</span>
            </label>
          </div>
          {sendError && <div className="err" role="alert">{sendError}</div>}
          <ComboSelect
            value={selected}
            options={options}
            onChange={setSelected}
            placeholder="Select a destination…"
          />
          <button
            type="submit"
            className="ca-btn ca-btn-solid"
            disabled={sendBusy}
            style={{ marginTop: 8 }}
          >
            {sendBusy ? 'Sending…' : hasChallenge ? 'Resend passcode' : 'Send passcode'}
          </button>
        </form>
      )}

      <form id="otp-form" onSubmit={(e) => { e.preventDefault(); if (code.length >= 6) onSubmit(code); }} noValidate>
        <label className="form-label" htmlFor="otp">
          {hasChallenge ? `Enter the code sent to your ${channelDesc}:` : 'Enter verification OTP code below:'}
        </label>
        {verifyError && <div className="err" role="alert">{verifyError}</div>}
        <OtpField inputRef={inputRef} code={code} setCode={setCode} disabled={!hasChallenge} />
        {hasChallenge && (
          <div className="field-err" style={{ color: 'var(--ai-muted)', marginTop: 4 }}>
            {expiresIn > 0 ? `Code expires in ${mm}:${ss}` : 'Code expired — request a new one.'}
            {onResend && (
              <button
                type="button"
                className="forgot-link"
                onClick={onResend}
                disabled={expiresIn > 240}
                style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 10 }}
              >
                Resend code
              </button>
            )}
          </div>
        )}
      </form>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MFA enroll-at-login body — first-time authenticator setup (or "Add another
// authenticator" for a shared account). Shows the QR code to scan plus a
// manual-entry fallback, then the same OtpField used everywhere else.
// ─────────────────────────────────────────────────────────────────────────────
interface MfaEnrollBodyProps {
  qr:         string | null;   // data: URL, or null while it's still generating
  secretBase32: string;
  code:       string;
  setCode:    (c: string) => void;
  error:      string | null;
  onSubmit:   (code: string) => void;
  /** Member name for THIS device (attribution on a shared account). */
  label:      string;
  setLabel:   (v: string) => void;
  /** The account being enrolled — drives the shared-account name hint. */
  account?:   string;
}

function MfaEnrollBody({ qr, secretBase32, code, setCode, error, onSubmit, label, setLabel, account }: MfaEnrollBodyProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  // A shared operator account (e.g. "service") is used by several people, so we
  // ask for the member's name — it's stamped on any report THIS device e-signs.
  const isShared = (account ?? '').trim().toLowerCase() === 'service';

  return (
    <form id="otp-form" onSubmit={(e) => { e.preventDefault(); if (code.length >= 6) onSubmit(code); }} noValidate>
      <p className="form-subtitle" style={{ marginTop: 0 }}>
        Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.), then enter the 6-digit code it shows.
      </p>

      <label className="form-label" htmlFor="enroll-name">
        Your name {isShared ? '(required for a shared account)' : '(optional — labels this device)'}
      </label>
      <div className="input-wrap" style={{ marginBottom: 10 }}>
        <input
          id="enroll-name"
          className="form-input"
          type="text"
          autoComplete="name"
          placeholder="e.g. Jane Doe"
          maxLength={100}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
      {isShared && (
        <p className="form-subtitle" style={{ marginTop: 0, fontSize: 11.5, color: 'var(--ai-muted)' }}>
          This is a shared login. Your name is stamped on any report you electronically sign from this device.
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
        {qr ? (
          <img src={qr} alt="Authenticator QR code" width={200} height={200} style={{ border: '1px solid var(--ai-line)', borderRadius: 8 }} />
        ) : (
          <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ai-muted)' }}>
            Generating QR code…
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginBottom: 4 }}>
          Can&rsquo;t scan? Enter this code manually:
        </div>
        <code style={{ fontSize: 13, letterSpacing: 1, background: 'var(--ai-row-zebra)', fontFamily: 'var(--ai-font-mono)', padding: '4px 8px', borderRadius: 4 }}>
          {secretBase32}
        </code>
      </div>

      <label className="form-label" htmlFor="otp">Enter the 6-digit code from your authenticator app:</label>
      {error && <div className="err" role="alert">{error}</div>}
      <OtpField inputRef={inputRef} code={code} setCode={setCode} />
    </form>
  );
}

function OtpField({
  inputRef, code, setCode, disabled = false,
}: { inputRef: RefObject<HTMLInputElement>; code: string; setCode: (c: string) => void; disabled?: boolean }) {
  return (
    <div className="input-wrap" style={{ marginTop: 6 }}>
      <span className="input-icon">
        <svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
      </span>
      <input
        id="otp"
        ref={inputRef}
        className="form-input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123456"
        maxLength={6}
        value={code}
        disabled={disabled}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        style={{ letterSpacing: 6, fontSize: 18, fontFamily: 'var(--ai-font-mono)' }}
      />
    </div>
  );
}
